/**
 * Nutrition API Transformer
 * Transforms getNutrition API response to frontend form format
 */

import { GetNutritionDataObject } from '@/types/NutritionApi.types';
import { INutritionForm, INutritionDocument, INutritionFootnoteStatement } from '@/types/Nutrition.types';
import { GetNutritionSectionsResponse } from '@/types/Nutrition.types';
import { getColumnsToDeclareTextForFactsPanelFormat } from '@/utils/columnsToDeclareByFactsPanelFormat';
import { parseCompositeNumberAndUnit, SERVING_QTY_NUMBER_REGEX } from "@/utils/nutritionCompositeField";
import { roundNumericString } from "@/utils/helper";
import { parsePdcaasForPayload, normalizePdcaasBlur } from "@/hooks/useNutrition";

/**
 * Extract nutrition data from the API response structure
 * The API returns data with nutritionTab array
 */
export const extractNutritionDataFromResponse = (
    apiResponse: GetNutritionSectionsResponse,
    nutritionTabId?: string | number
): GetNutritionDataObject | null => {
    // Handle format with nutritionTab array
    if ('nutritionTab' in apiResponse && Array.isArray(apiResponse.nutritionTab) && apiResponse.nutritionTab.length > 0) {
        return nutritionTabId
            ? (apiResponse.nutritionTab.find((tab: any) => String(tab.id) === String(nutritionTabId)) || apiResponse.nutritionTab[0]) as any
            : apiResponse.nutritionTab[0] as any;
    }
    
    console.warn(' [extractNutritionDataFromResponse] Empty or invalid API response');
    return null;
};

/**
 * Parse stringified footnote statement back to INutritionFootnoteStatement
 * API can send either:
 * 1. Simple string: { text: "Keep refrigerated" }
 * 2. Stringified JSON: { text: "{ option: {...}, inputs: [...] }" }
 */
export const parseFootnoteStatement = (text: string): INutritionFootnoteStatement | null => {
    try {
        // Try to parse as JSON first (stringified object format)
        const parsed = JSON.parse(text);
        
        // Validate the structure for complex footnote statements
        if (parsed && parsed.option && Array.isArray(parsed.inputs)) {
            return {
                option: {
                    id: parsed.option.id || '',
                    label: parsed.option.label || '',
                    template: parsed.option.template || []
                },
                inputs: parsed.inputs
            };
        }
        
        console.warn('Invalid footnote statement structure:', parsed);
        return null;
    } catch (error) {
        // If JSON parsing fails, treat as a simple string comment
        // Convert simple string to a basic footnote statement
        console.log(' [parseFootnoteStatement] Treating as simple string:', text);
        
        return {
            option: {
                id: `simple_comment_${Date.now()}`,
                label: text,
                template: [{ type: 'text', value: text }]
            },
            inputs: []
        };
    }
};

/**
 * Transform attachments from API format to frontend documents format
 */
export const transformAttachmentsToDocuments = (
    attachments?: Array<{
        documentTitle?: string;
        document?: {
            id: string;
            filename?: string;
        };
        id?: string | number; // Some API responses might have id at top level
    }>
): INutritionDocument[] => {
    if (!attachments || attachments.length === 0) {
        return [];
    }
    
    return attachments.map(attachment => {
        // Handle different API response structures
        const documentId = attachment.document?.id || attachment.id;
        const filename = attachment.document?.filename || attachment.documentTitle || '';
        const documentTitle = attachment.documentTitle || attachment.document?.filename || 'Document';
        
        return {
            id: String(documentId || ''),
            fileName: filename || documentTitle, // Use filename if available, fallback to documentTitle
            type: documentTitle,
            uploadDate: new Date().toISOString() // API doesn't provide upload date
        };
    }).filter(doc => doc.id); // Filter out documents without IDs
};

/**
 * Transform documents from frontend format to API format
 */
export const transformDocumentsToAttachments = (
    documents?: INutritionDocument[]
): Array<{
    documentTitle: string;
    document: {
        id: string;
        filename: string;
    };
}> => {
    if (!documents || documents.length === 0) {
        return [];
    }
    
    return documents.map(doc => ({
        documentTitle: doc.type || 'Document',
        document: {
            id: doc.id,
            filename: doc.fileName
        }
    }));
};

/**
 * Main transformer: API response -> Frontend form
 * Handles GetNutritionSectionsResponse with nutritionTab array
 */
export const transformGetNutritionResponse = (
    apiResponse: GetNutritionSectionsResponse,
    nutritionTabId?: string | number
): Partial<INutritionForm> => {
    // Extract nutrition data object from response
    // Handle format with nutritionTab array
    let nutritionData: GetNutritionDataObject | null = null;
    
    if ('nutritionTab' in apiResponse && Array.isArray(apiResponse.nutritionTab)) {
        // GetNutritionSectionsResponse with nutritionTab array
        // Find section by nutritionTabId if provided, otherwise use first
        nutritionData = nutritionTabId
            ? (apiResponse.nutritionTab.find((tab: any) => String(tab.id) === String(nutritionTabId)) || apiResponse.nutritionTab[0] || null) as any
            : (apiResponse.nutritionTab[0] || null) as any;
    } else {
        // Direct object format (fallback)
        nutritionData = apiResponse as any;
    }
    
    if (!nutritionData) {
        console.warn(' [transformGetNutritionResponse] No nutrition data found in response');
        return {};
    }
    
    // Parse footnote statements (supports both simple strings and stringified JSON)
    const footnoteStatements: INutritionFootnoteStatement[] = [];
    if (nutritionData.nutritionCommentsText && nutritionData.nutritionCommentsText.length > 0) {
        nutritionData.nutritionCommentsText.forEach(item => {
            const parsed = parseFootnoteStatement(item.text);
            if (parsed) {
                footnoteStatements.push(parsed);
            }
        });
    }
    
    // Transform attachments to documents
    const documents = transformAttachmentsToDocuments(nutritionData.attachments);
    
    // Combine serving quantity with UOM
    const servingQuantityForCalculation = nutritionData.servingQuantityForCalculations && nutritionData.servingQuantityForCalculationsUom
        ? `${nutritionData.servingQuantityForCalculations} ${nutritionData.servingQuantityForCalculationsUom}`
        : '';
    
    // Combine serving quantity column 2 with UOM
    const servingQuantityColumn2 = nutritionData.servingQuantityForCalculationsCol2 && nutritionData.servingQuantityForCalculationsCol2Uom
        ? `${nutritionData.servingQuantityForCalculationsCol2} ${nutritionData.servingQuantityForCalculationsCol2Uom}`
        : '';
    
    // Extract packCopy from API response (if wrapped format)
    let packCopy: { id: string; filename: string } | undefined = undefined;
    if ('packCopy' in apiResponse && apiResponse.packCopy) {
        const packCopyData = (apiResponse as any).packCopy;
        if (packCopyData && typeof packCopyData.id === 'string' && typeof packCopyData.filename === 'string') {
            packCopy = {
                id: packCopyData.id,
                filename: packCopyData.filename
            };
        }
    }
    
    // Map to frontend format
    const frontendForm: Partial<INutritionForm> = {
        // ID from API (for subsequent updates)
        id: nutritionData.id,
        
        // Genesis ID from API (for NFP label generation)
        genesisId: nutritionData.genesisId,
        
        // Validation status from API (string; coerce to boolean for form)
        isNutritionValidated: nutritionData.isNutritionValidated === "true",

        // Section name (if present in response)
        sectionName: (nutritionData as any)?.sectionName || '',
        
        // Basic fields
        nutritionDataSubmissionMethod: nutritionData.howNutritionDataBeingSubmitted || '',
        factsPanelFormat: nutritionData.factsPanelFormatForLabel || '',
        headingText: nutritionData.headingText || '',
        footnote: nutritionData.footnote || '',
        calorieContent: (nutritionData as any).calorieContent || '',
        columnsToDeclare: getColumnsToDeclareTextForFactsPanelFormat(
            nutritionData.factsPanelFormatForLabel || ''
        ),
        servingSizeAndHouseholdMeasure: nutritionData.servingSizeHouseholdMeasure || '',
        servingsPerContainer: nutritionData.servingsPerContainer || '',
        servingQuantityForCalculation: servingQuantityForCalculation,
        dvCalculationBasis: nutritionData.percentageDvCalculatedBasedOn || '',
        pdcaas: normalizePdcaasBlur((nutritionData as any).pdcaas),
        rdiCalculationBasis: nutritionData.percentageRdiCalculatedBasedOn || '',
        calculateAmounts: nutritionData.calculateAmounts || '',
        dataSource: nutritionData.sourceOfData || '',
        
        // Dual column fields
        column1Heading: nutritionData.column1Heading ?? '',
        column2Heading: nutritionData.headingTextCol2 || '',
        amountPreparedText: nutritionData.amountPreparedText || '',
        servingQuantityColumn2: servingQuantityColumn2,
        
        // Pet food guaranteed analysis textarea mapped from API nutritionComments
        guaranteedAnalysis: nutritionData.guaranteedAnalysis || '',
        
        // Parsed footnote statements
        footnoteAdditionStatements: footnoteStatements,
        
        // Transformed documents
        documents: documents,
        
        // Pack Copy document (from API response wrapper)
        packCopy: packCopy
    };
    
    return frontendForm;
};

/**
 * Reverse transformer: Frontend form -> API payload
 * For saving nutrition data (returns the data object, not the wrapper)
 */
export const transformToNutritionApiPayload = (
    frontendForm: INutritionForm,
    productSpecificationId: number,
    assessmentId?: string
): Partial<GetNutritionDataObject> => {
    // Stringify footnote statements
    const nutritionCommentsText = frontendForm.footnoteAdditionStatements?.map(statement => ({
        text: JSON.stringify({
            option: statement.option,
            inputs: statement.inputs
        })
    })) || [];
    
    // Transform documents to attachments
    const attachments = transformDocumentsToAttachments(frontendForm.documents);
    
    // Extract numeric value + unit from composite field.
    // If numeric is empty/invalid, do not send a default unit.
    const parsedServing = parseCompositeNumberAndUnit(frontendForm.servingQuantityForCalculation);
    const servingIsNumeric = SERVING_QTY_NUMBER_REGEX.test(String(parsedServing?.value ?? "").trim());
    const servingQuantityForCalculations = servingIsNumeric ? String(parsedServing?.value ?? "").trim() : '';
    const servingQuantityForCalculationsUom =
        servingIsNumeric && servingQuantityForCalculations
            ? (String(parsedServing?.unit ?? "").trim() || 'g')
            : '';
    
    // Map to API format (data object, not wrapper)
    const apiPayload: Partial<GetNutritionDataObject> = {
        productSpecificationId: productSpecificationId,
        howNutritionDataBeingSubmitted: frontendForm.nutritionDataSubmissionMethod || '',
        factsPanelFormatForLabel: frontendForm.factsPanelFormat || '',
        headingText: frontendForm.headingText || '',
        footnote: frontendForm.footnote || '',
        calorieContent: frontendForm.calorieContent || '',
        columnsToBeDeclared: frontendForm.columnsToDeclare || '',
        servingSizeHouseholdMeasure: frontendForm.servingSizeAndHouseholdMeasure || '',
        servingsPerContainer: frontendForm.servingsPerContainer || '',
        servingQuantityForCalculations: servingQuantityForCalculations,
        servingQuantityForCalculationsUom: servingQuantityForCalculationsUom,
        percentageDvCalculatedBasedOn: frontendForm.dvCalculationBasis || '',
        ...(() => {
            const pdcaas = parsePdcaasForPayload(frontendForm.pdcaas);
            return pdcaas !== undefined ? { pdcaas } : {};
        })(),
        percentageRdiCalculatedBasedOn: frontendForm.rdiCalculationBasis || '',
        calculateAmounts: frontendForm.calculateAmounts || '',
        sourceOfData: frontendForm.dataSource || '',
        
        // Dual column fields
        column1Heading: frontendForm.column1Heading || '',
        headingTextCol2: frontendForm.column2Heading || '',
        amountPreparedText: frontendForm.amountPreparedText || '',
        // Extract numeric value and unit from servingQuantityColumn2 (similar to servingQuantityForCalculation)
        servingQuantityForCalculationsCol2: (() => {
            const col2Value = (frontendForm.servingQuantityColumn2 || '').trim();
            if (!col2Value) return '';
            const firstSpaceIdx = col2Value.search(/\s/);
            if (firstSpaceIdx === -1) {
                // No unit provided, return the value as-is if numeric
                const isNumeric = /^\d*\.?\d+$/.test(col2Value);
                return isNumeric ? col2Value : '';
            }
            const numericPart = col2Value.slice(0, firstSpaceIdx).trim();
            const isNumeric = /^\d*\.?\d+$/.test(numericPart);
            return isNumeric ? numericPart : '';
        })(),
        servingQuantityForCalculationsCol2Uom: (() => {
            const col2Value = (frontendForm.servingQuantityColumn2 || '').trim();
            if (!col2Value) return 'g';
            const firstSpaceIdx = col2Value.search(/\s/);
            if (firstSpaceIdx === -1) {
                // No unit provided
                return 'g';
            }
            const unitPart = col2Value.slice(firstSpaceIdx + 1).trim();
            return unitPart || 'g';
        })(),
        
        // Pet food specific - map guaranteedAnalysis to nutritionComments for API
        nutritionComments: frontendForm.guaranteedAnalysis || '',
        
        // Stringified footnote statements
        nutritionCommentsText: nutritionCommentsText,
        
        // Transformed attachments
        attachments: attachments
    };
    
    return apiPayload;
};

/**
 * Field mapping reference for documentation
 */
export const NUTRITION_FIELD_MAPPING = {
    // API Field -> Frontend Field
    howNutritionDataBeingSubmitted: 'nutritionDataSubmissionMethod',
    factsPanelFormatForLabel: 'factsPanelFormat',
    headingText: 'headingText',
    footnote: 'footnote',
    columnsToBeDeclared: 'columnsToDeclare',
    servingSizeHouseholdMeasure: 'servingSizeAndHouseholdMeasure',
    servingsPerContainer: 'servingsPerContainer',
    'servingQuantityForCalculations + servingQuantityForCalculationsUom': 'servingQuantityForCalculation',
    percentageDvCalculatedBasedOn: 'dvCalculationBasis',
    pdcaas: 'pdcaas',
    percentageRdiCalculatedBasedOn: 'rdiCalculationBasis',
    calculateAmounts: 'calculateAmounts',
    sourceOfData: 'dataSource',
    
    // Dual column fields
    column1Heading: 'column1Heading',
    headingTextCol2: 'column2Heading',
    amountPreparedText: 'amountPreparedText',
    'servingQuantityForCalculationsCol2 + servingQuantityForCalculationsCol2Uom': 'servingQuantityColumn2',
    
    // Complex transformations
    'nutritionCommentsText[]': 'footnoteAdditionStatements (parsed from stringified JSON)',
    'attachments[]': 'documents (transformed structure)',
    
    // Pet food
    nutritionComments: 'guaranteedAnalysis (frontend field maps to nutritionComments in API)'
} as const;

